// @flow

import * as React from 'react';
import { Rifm } from 'rifm';
import FilledInput from '@material-ui/core/FilledInput';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { useSelectionBound } from '../hooks/selection-bound.js';

type Props = {|
  variant?: 'filled' | 'outlined',
  value: string,
  onChange: string => void,
  onBlur?: () => void,
|};

const SUPPORTED_FORMATS = [
  // Switzerland
  '+41 XX XXX XX XX',
  // Russia
  '+7 XXX XXX XX XX',
  // France
  '+33 X XX XX XX XX',
  // Britain
  '+44 XX XXXX XXXX',
  // Germany
  // Too many rules will just split country code and 11 numbers
  '+49 XXXXXXXXXXX',
];

const compiledSupportedFormats = SUPPORTED_FORMATS.map(mask => {
  const maskA = mask.split(' ');
  const prefix = mask.replace(/[^\d]+/gi, '');
  const spaceIndexes = maskA.reduce((r, v, index) => {
    r.push((index > 0 ? r[index - 1] : 0) + v.length);
    return r;
  }, ([]: Array<number>));

  spaceIndexes.pop();

  return {
    prefix,
    spaceIndexes,
    size: mask.length,
  };
});

const formatPhone = (str: string): string => {
  if (str === '') {
    return '+';
  }

  const MAX_POSSIBLE_LENGTH = 16;

  const clean = str.replace(/[^\d]+/gi, '').substr(0, MAX_POSSIBLE_LENGTH);
  const compiledFormatter = compiledSupportedFormats.find(format =>
    clean.startsWith(format.prefix),
  );

  if (compiledFormatter == null) {
    return '+' + clean;
  }

  return (
    '+' +
    clean
      .split('')
      .reduce(
        (r, v, index) =>
          compiledFormatter.spaceIndexes.some(si => si === index + 1)
            ? `${r} ${v}`
            : `${r}${v}`,
        '',
      )
      .substr(0, compiledFormatter.size - 1)
  );
};

export const SWISS_PHONE_PREFIX = '+41';
export const FRENCH_PHONE_PREFIX = '+33';

export function getPhonePrefix(countryCode: string): string {
  switch (countryCode) {
    case 'FR':
      return FRENCH_PHONE_PREFIX;
    default:
      return SWISS_PHONE_PREFIX;
  }
}

export const PhoneInput = (props: Props): React.Node => {
  const phoneValue = formatPhone(props.value);
  const inputRef = React.useRef(null);
  const { variant = 'outlined' } = props;

  useSelectionBound(inputRef, 1, -1);

  return (
    <Rifm
      accept={/[\d+]+/g}
      format={formatPhone}
      value={phoneValue}
      onChange={props.onChange}
    >
      {({ value, onChange }) =>
        variant === 'filled' ? (
          <FilledInput
            inputRef={inputRef}
            type="tel"
            autoComplete="tel"
            value={value}
            onChange={onChange}
            onBlur={props.onBlur}
          />
        ) : (
          <OutlinedInput
            inputRef={inputRef}
            labelWidth={0}
            type="tel"
            autoComplete="tel"
            value={value}
            onChange={onChange}
            onBlur={props.onBlur}
          />
        )
      }
    </Rifm>
  );
};
